
import { useEffect, useState } from 'react';
import { api } from '../../api/api';
import AdminHeader from '../../components/adminHeader';
import AdminFooter from '../../components/adminFooter';
import AddAssetModal from '../../components/modal/AddAssetModal';
import UpdateAssetModal from '../../components/modal/UpdateAssetModal';

interface Asset {
  id: number;
  name: string;
  description: string;
  price: number;
  image_path: string;
}

const AdminAssetsView = () => {
  const [allAssets, setAllAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const assetsPerPage = 24;
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const fetchAssets = async () => {
    try {
      const response = await api.get('/api/all/assets');
      setAllAssets(response.data);
    } catch (err) {
      setError('Failed to fetch assets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  const filteredAssets = allAssets.filter(asset =>
    asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastAsset = currentPage * assetsPerPage;
  const indexOfFirstAsset = indexOfLastAsset - assetsPerPage;
  const currentAssets = filteredAssets.slice(indexOfFirstAsset, indexOfLastAsset);

  const totalPages = Math.ceil(filteredAssets.length / assetsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleAssetAdded = () => {
    fetchAssets();
    setIsAddModalOpen(false);
  };

  const handleAssetUpdated = () => {
    fetchAssets();
    setIsUpdateModalOpen(false);
    setSelectedAsset(null);
  };

  const openUpdateModal = (asset: Asset) => {
    setSelectedAsset(asset);
    setIsUpdateModalOpen(true);
  };

  if (loading) {
    return <div className="text-center text-white text-2xl mt-10">Loading assets...</div>;
  }

  if (error) {
    return <div className="text-center text-red-500 text-2xl mt-10">Error: {error}</div>;
  }

  return (
    <div className="w-full h-full flex flex-col bg-gray-900 font-mono text-white">
      <AdminHeader />
      <div className="flex-grow p-8">
        <h1 className="text-5xl font-bold mb-10 text-center text-yellow-400 drop-shadow-lg">Admin Assets View</h1>

        <div className="mb-8 flex justify-center items-center space-x-4">
          <input
            type="text"
            placeholder="Search assets..."
            className="p-3 rounded-lg bg-gray-800 border border-gray-700 text-white w-full max-w-md focus:outline-none focus:border-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105"
          >
            Add New Asset
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {currentAssets.map((asset) => (
            <div key={asset.id} className="bg-gray-800 p-4 rounded-lg border-2 border-gray-700 shadow-lg transform transition-all duration-300 hover:scale-105 flex flex-col items-center text-center">
              <img src={`${process.env.REACT_APP_API_URL}/api/files/${asset.image_path}`} alt={asset.name} className="w-32 h-32 object-cover rounded-md mb-4 border border--gray-600" />
              <h2 className="text-xl font-bold text-white mb-2">{asset.name}</h2>
              <p className="text-gray-400 text-sm mb-3 line-clamp-2">{asset.description}</p>
              <p className="text-green-400 text-lg font-semibold mb-4">${asset.price}</p>
              <button onClick={() => openUpdateModal(asset)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition-colors duration-300">
                Edit
              </button>
            </div>
          ))}
        </div>

        <div className="flex justify-center space-x-4 mt-8">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => paginate(i + 1)}
              className={`py-2 px-4 rounded-lg font-bold ${currentPage === i + 1 ? 'bg-blue-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
      <AdminFooter />

      <AddAssetModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAssetAdded={handleAssetAdded}
      />

      <UpdateAssetModal
        isOpen={isUpdateModalOpen}
        onClose={() => { setIsUpdateModalOpen(false); setSelectedAsset(null); }}
        onAssetUpdated={handleAssetUpdated}
        asset={selectedAsset}
      />
    </div>
  );
};

export default AdminAssetsView;
